import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CheckCircle,
  Clock,
  ImagePlus,
  Loader2,
  X,
  Upload,
  FileCheck,
  AlertTriangle,
  Timer,
  Banknote,
  Calculator,
} from 'lucide-react';
import { TipoEventoOS } from '@/types/ordemServico';
import {
  fetchOSResumoFinalizacao,
  fetchItensOSComServico,
  uploadComprovativoPagamento,
  uploadFotoServico,
  finalizarOS,
  atualizarItemOSPorHora,
  registrarEventoOS,
} from '@/services/ordens-servico.service';
import { sendOSTemplate } from '@/services/whatsapp.service';
import { useOrganization } from '@/hooks/useOrganization';
import { formatarValor } from '@/types/servico';
import { useAnalytics } from '@/hooks/useAnalytics';
import { formatarTempo, calcularValorFinal, calcularTempoTotal, arredondarCentimos } from './finalizar-os-logic';

const MINUTOS_FATURAVEIS_SUSPEITO = 5;

interface FinalizarServicoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  osId: string;
  numeroOs: string;
}

export function FinalizarServicoDialog({
  open,
  onOpenChange,
  osId,
  numeroOs,
}: FinalizarServicoDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  const { requireOrgId } = useOrganization();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const comprovativoInputRef = useRef<HTMLInputElement>(null);
  
  const [observacoes, setObservacoes] = useState('');
  const [fotos, setFotos] = useState<File[]>([]);
  const [fotoPreviews, setFotoPreviews] = useState<string[]>([]);
  const [comprovativo, setComprovativo] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [tempoAtual, setTempoAtual] = useState(0);
  const [minutosFaturaveis, setMinutosFaturaveis] = useState(0);
  const [faturavelEditado, setFaturavelEditado] = useState(false);

  // Buscar dados da OS (data_inicio_execucao e valor atual)
  const { data: osData, isLoading: isLoadingOS } = useQuery({
    queryKey: ['ordem-servico-finalizar', osId],
    queryFn: () => fetchOSResumoFinalizacao(osId),
    enabled: open && !!osId,
  });

  // Buscar itens da OS para verificar se tem serviços por hora
  const { data: itensOS, isLoading: isLoadingItens } = useQuery({
    queryKey: ['itens-os-finalizar', osId],
    queryFn: () => fetchItensOSComServico(osId),
    enabled: open && !!osId,
  });

  // OS reaberta = já foi concluída antes (reabrirOS não limpa data_fim_execucao).
  const jaConcluida = !!osData?.data_fim_execucao;
  const tempoAnterior = jaConcluida ? (osData?.tempo_servico_minutos ?? 0) : 0;

  // Calcular tempo decorrido em tempo real
  useEffect(() => {
    if (!open || !osData?.data_inicio_execucao) {
      setTempoAtual(0);
      return;
    }

    const calcularTempo = () => {
      const inicio = new Date(osData.data_inicio_execucao);
      const agora = new Date();
      const diffMs = agora.getTime() - inicio.getTime();
      return Math.max(0, Math.floor(diffMs / 60000)); // Minutos
    };

    setTempoAtual(calcularTempo());

    // Atualizar a cada minuto
    const interval = setInterval(() => {
      setTempoAtual(calcularTempo());
    }, 60000);

    return () => clearInterval(interval);
  }, [open, osData?.data_inicio_execucao]);

  // Default das horas a faturar = cronómetro de parede, enquanto o prestador não edita.
  useEffect(() => {
    if (!faturavelEditado) setMinutosFaturaveis(tempoAtual);
  }, [tempoAtual, faturavelEditado]);

  // Repõe a flag de edição quando o diálogo fecha.
  useEffect(() => {
    if (!open) setFaturavelEditado(false);
  }, [open]);

  // Calcular valor final considerando serviços por hora (sobre as horas confirmadas)
  const { valorFinal, valorDiferenca, temServicosPorHora } = useMemo(
    () => calcularValorFinal({
      itensOS,
      minutosFaturaveis,
      valorOriginal: osData?.valor_total || 0,
      jaConcluida,
    }),
    [itensOS, minutosFaturaveis, osData?.valor_total, jaConcluida],
  );

  const tempoTotalExibido = useMemo(
    () => calcularTempoTotal({ tempoAnterior, elapsedMinutos: tempoAtual, jaConcluida }),
    [tempoAnterior, tempoAtual, jaConcluida],
  );

  // O prestador só indica as "Horas a faturar" numa OS MISTA — quando há um
  // serviço por_hora E pelo menos um serviço de outro tipo (fixo/m²). Aí é
  // preciso separar o tempo da parte por hora do tempo gasto no serviço por
  // unidade (senão o cliente pagaria à hora o serviço de preço fixo).
  // Numa OS só com por_hora, o cronómetro entra automático (ninguém edita).
  const temServicoNaoPorHora = useMemo(
    () => (itensOS ?? []).some((i) => i.servico && i.servico.tipo_preco !== 'por_hora'),
    [itensOS],
  );
  const mostrarHorasFaturaveis = !jaConcluida && temServicosPorHora && temServicoNaoPorHora;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    // Limitar a 5 fotos
    const novasFotos = [...fotos, ...files].slice(0, 5);
    setFotos(novasFotos);
    
    // Criar previews
    const novosPreview = novasFotos.map(file => URL.createObjectURL(file));
    setFotoPreviews(prev => {
      // Limpar URLs antigos
      prev.forEach(url => URL.revokeObjectURL(url));
      return novosPreview;
    });
  };

  const handleComprovativoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar tipo de arquivo (imagem ou PDF)
      const tiposPermitidos = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!tiposPermitidos.includes(file.type)) {
        toast.error(t('orders.finalize.unsupportedFormat'));
        return;
      }
      setComprovativo(file);
    }
  };

  const removerFoto = (index: number) => {
    setFotos(prev => prev.filter((_, i) => i !== index));
    setFotoPreviews(prev => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const finalizarServico = useMutation({
    mutationFn: async () => {
      if (!jaConcluida && !comprovativo) {
        throw new Error(t('orders.finalize.paymentProofRequired'));
      }
      if (!osData?.data_inicio_execucao) {
        throw new Error(t('orders.finalize.startDateMissing'));
      }

      setUploading(true);

      // 1. Upload comprovativo (só em OS não reaberta / quando há ficheiro)
      let comprovativoUrl: string | null = null;
      if (comprovativo) {
        try {
          comprovativoUrl = await uploadComprovativoPagamento({ osId, file: comprovativo });
        } catch (e) {
          console.error('Erro ao fazer upload do comprovativo:', e);
          throw new Error(t('orders.finalize.uploadError'));
        }
      }

      // 2. Upload photos (best-effort, skip failures)
      const fotosUrls: string[] = [];
      for (const foto of fotos) {
        const url = await uploadFotoServico({ osId, file: foto });
        if (url) fotosUrls.push(url);
      }

      // 3. Tempo: ronda atual a partir do início (cronómetro de parede da visita).
      const dataFim = new Date();
      const dataInicio = new Date(osData.data_inicio_execucao);
      const tempoRondaMinutos = Math.max(
        1,
        Math.floor((dataFim.getTime() - dataInicio.getTime()) / 60000),
      );
      // Métrica gravada em tempo_servico_minutos: cronómetro da visita (reaberta preserva o original).
      const tempoTotalMinutos = calcularTempoTotal({
        tempoAnterior,
        elapsedMinutos: tempoRondaMinutos,
        jaConcluida,
      });

      // 4. Finalize OS (comprovativoUrl null preserva o anterior)
      await finalizarOS({
        osId,
        tempoServicoMinutos: tempoTotalMinutos,
        dataFim,
        fotosUrls,
        observacoes,
        comprovativoUrl,
        valorTotal: valorFinal,
      });

      // 4b. Fire-and-forget: notify cliente via WhatsApp template. NEVER blocks
      // the dialog flow and NEVER throws (service contract). Falha silenciosa.
      sendOSTemplate(osId).catch((err) =>
        console.warn('[FinalizarServicoDialog] sendOSTemplate threw unexpectedly', err),
      );

      // 5. Update hourly items — só quando NÃO é reaberta (reaberta mantém valor).
      // Preço por horas CONFIRMADAS pelo prestador (minutosFaturaveis), proporcional exato.
      if (!jaConcluida && temServicosPorHora && itensOS) {
        const horasFaturaveis = minutosFaturaveis / 60;
        for (const item of itensOS) {
          const servico = item.servico;
          if (servico?.tipo_preco === 'por_hora') {
            const novoValorTotal = arredondarCentimos(horasFaturaveis * servico.valor_base);
            await atualizarItemOSPorHora({
              itemId: item.id,
              quantidade: arredondarCentimos(horasFaturaveis),
              valorTotal: novoValorTotal,
            });
          }
        }
      }

      // 6. Register event
      await registrarEventoOS({
        osId,
        orgId: requireOrgId(),
        tipo: 'os_concluida' as TipoEventoOS,
        descricao: jaConcluida
          ? 'Serviço reaberto finalizado'
          : 'Serviço finalizado com comprovativo de pagamento',
        detalhes: {
          tempo_minutos: tempoTotalMinutos,
          tempo_ronda_minutos: tempoRondaMinutos,
          tempo_anterior_minutos: tempoAnterior,
          minutos_faturaveis: minutosFaturaveis,
          tempo_calculado_automaticamente: true,
          reaberta: jaConcluida,
          qtd_fotos: fotosUrls.length,
          tem_observacao: !!observacoes,
          tem_comprovativo: !!comprovativoUrl,
          valor_final: valorFinal,
          valor_original: osData.valor_total,
          valor_diferenca: valorDiferenca,
          tem_servicos_por_hora: temServicosPorHora,
        },
      });

      return { fotosUrls, tempoTotalMinutos, valorFinal };
    },
    onSuccess: (result) => {
      track('os_completed', {
        tempo_minutos: result.tempoTotalMinutos,
        valor_final: result.valorFinal,
        tem_servicos_por_hora: temServicosPorHora,
        qtd_fotos: result.fotosUrls.length,
      });

      queryClient.invalidateQueries({ queryKey: ['ordens-servico'] });
      // Prefixos: as queries de detalhe são ['ordem-servico'|'eventos-os'|'itens-os', orgId, id];
      // invalidar com [..., osId] não correspondia (orgId está na posição 1).
      queryClient.invalidateQueries({ queryKey: ['ordem-servico'] });
      queryClient.invalidateQueries({ queryKey: ['eventos-os'] });
      queryClient.invalidateQueries({ queryKey: ['itens-os'] });

      const tempoFormatado = formatarTempo(result.tempoTotalMinutos);
      toast.success(t('orders.finalize.success', { time: tempoFormatado }));
      
      // Limpar e fechar
      setObservacoes('');
      setFotos([]);
      setComprovativo(null);
      fotoPreviews.forEach(url => URL.revokeObjectURL(url));
      setFotoPreviews([]);
      onOpenChange(false);
    },
    onError: (error: Error) => {
      console.error('Erro ao finalizar serviço:', error);
      toast.error(error.message || t('orders.toast.finalizeError'));
    },
    onSettled: () => {
      setUploading(false);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    finalizarServico.mutate();
  };

  // Numa OS reaberta o comprovante é dispensado (serviço já pago).
  const isFormValid = (jaConcluida || !!comprovativo) && !!osData?.data_inicio_execucao;
  const isLoading = isLoadingOS || isLoadingItens;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-primary" />
            {t('orders.finalize.title')}
          </DialogTitle>
          <DialogDescription>
            {t('orders.finalize.description', { number: numeroOs })}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Resumo do Tempo e Valor */}
            <Card className="bg-muted/30 border-primary/20">
              <CardContent className="pt-4 space-y-3">
                {/* Tempo de execução automático */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <Timer className="h-4 w-4 text-primary" />
                    <span className="text-muted-foreground">{t('orders.finalize.executionTime')}</span>
                  </div>
                  <Badge variant="secondary" className="text-sm font-mono">
                    <Clock className="h-3 w-3 mr-1" />
                    {formatarTempo(tempoTotalExibido)}
                  </Badge>
                </div>

                {jaConcluida && (
                  <p className="text-xs text-muted-foreground text-right">
                    {t('orders.finalize.correctionTime')}
                  </p>
                )}

                {!osData?.data_inicio_execucao && (
                  <div className="flex items-start gap-2 p-2 bg-destructive/10 rounded-md">
                    <AlertTriangle className="h-4 w-4 text-destructive mt-0.5" />
                    <p className="text-xs text-destructive">
                      {t('orders.finalize.startDateError')}
                    </p>
                  </div>
                )}

                <Separator />

                {/* Valor */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <Banknote className="h-4 w-4 text-primary" />
                    <span className="text-muted-foreground">{t('orders.finalize.serviceValue')}</span>
                  </div>
                  <span className="text-lg font-semibold text-primary">
                    {formatarValor(valorFinal)}
                  </span>
                </div>

                {/* Indicador de recálculo para serviços por hora */}
                {temServicosPorHora && valorDiferenca !== 0 && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Calculator className="h-3 w-3" />
                    <span>
                      {t('orders.finalize.updatedByTime')}{' '}
                      ({valorDiferenca > 0 ? '+' : ''}{formatarValor(valorDiferenca)})
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Horas a faturar — só quando há serviço por_hora e não é reaberta */}
            {mostrarHorasFaturaveis && (
              <div className="space-y-1">
                <Label className="flex items-center gap-2 text-sm">
                  <Timer className="h-4 w-4" />
                  {t('orders.finalize.billableHours')}
                </Label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    value={Math.floor(minutosFaturaveis / 60)}
                    onChange={(e) => {
                      const h = Math.max(0, Number(e.target.value) || 0);
                      setFaturavelEditado(true);
                      setMinutosFaturaveis(h * 60 + (minutosFaturaveis % 60));
                    }}
                    className="w-16 rounded-md border bg-background px-2 py-1 text-sm"
                    aria-label={t('orders.finalize.billableHours')}
                  />
                  <span className="text-sm text-muted-foreground">{t('orders.finalize.unitHour')}</span>
                  <input
                    type="number"
                    min={0}
                    max={59}
                    value={minutosFaturaveis % 60}
                    onChange={(e) => {
                      const m = Math.min(59, Math.max(0, Number(e.target.value) || 0));
                      setFaturavelEditado(true);
                      setMinutosFaturaveis(Math.floor(minutosFaturaveis / 60) * 60 + m);
                    }}
                    className="w-16 rounded-md border bg-background px-2 py-1 text-sm"
                    aria-label={t('orders.finalize.unitMinute')}
                  />
                  <span className="text-sm text-muted-foreground">{t('orders.finalize.unitMinute')}</span>
                </div>
                <p className="text-xs text-muted-foreground">{t('orders.finalize.billableHoursHelp')}</p>
                {(minutosFaturaveis < MINUTOS_FATURAVEIS_SUSPEITO || minutosFaturaveis > tempoAtual) && (
                  <div className="flex items-start gap-2 p-2 bg-amber-500/10 rounded-md">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                    <p className="text-xs text-amber-700 dark:text-amber-500">
                      {t('orders.finalize.shortTimeWarning')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Comprovativo de pagamento - OBRIGATÓRIO (dispensado em OS reaberta) */}
            {!jaConcluida && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <FileCheck className="h-4 w-4" />
                  {t('orders.finalize.paymentProof')}
                </Label>

                <input
                  ref={comprovativoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  className="hidden"
                  onChange={handleComprovativoChange}
                />

                {comprovativo ? (
                  <div className="flex items-center gap-2 p-3 border rounded-md bg-muted/30">
                    <FileCheck className="h-5 w-5 text-primary" />
                    <span className="text-sm flex-1 truncate">{comprovativo.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setComprovativo(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => comprovativoInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {t('orders.finalize.selectProof')}
                  </Button>
                )}

                {!comprovativo && (
                  <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-md">
                    <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-destructive">
                      {t('orders.finalize.paymentProofRequired')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Observações */}
            <div className="space-y-2">
              <Label htmlFor="observacoes">{t('orders.finalize.serviceNotes')}</Label>
              <Textarea
                id="observacoes"
                placeholder={t('orders.finalize.serviceNotesPlaceholder')}
                value={observacoes}
                onChange={e => setObservacoes(e.target.value)}
                rows={3}
              />
            </div>

            {/* Upload de fotos */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <ImagePlus className="h-4 w-4" />
                {t('orders.finalize.photos')}
              </Label>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFileChange}
              />
              
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => fileInputRef.current?.click()}
                disabled={fotos.length >= 5}
              >
                <Upload className="h-4 w-4 mr-2" />
                {t('orders.finalize.addPhotos', { count: fotos.length })}
              </Button>
              
              {/* Preview das fotos */}
              {fotoPreviews.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {fotoPreviews.map((preview, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={preview}
                        alt={`Foto ${index + 1}`}
                        className="w-full h-20 object-cover rounded-md"
                      />
                      <button
                        type="button"
                        onClick={() => removerFoto(index)}
                        className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={uploading}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={uploading || !isFormValid}
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('orders.finalize.finalizing')}
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {t('orders.finalize.confirm')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
